// The "Add photo" control of the identity page (PRD-0031 slice 05).
//
// `capture="environment"` is what makes a phone open its back camera instead of
// the file browser; a desktop ignores it and shows the picker, so one input
// serves both. `multiple` keeps a batch from the gallery in one go.
import { Camera, Loader2 } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { AttachIdentityDocumentsState } from '@/features/identities/hooks/useAttachIdentityDocuments'

export interface AddPhotoButtonProps {
  /** The phase the upload hook is reporting — sending, extracting, or idle. */
  state: AttachIdentityDocumentsState
  onFiles: (files: File[]) => void
}

/** The label follows the phase, so the button *is* the progress indicator. */
function useLabel({ uploading, processing, uploaded, total }: AttachIdentityDocumentsState) {
  const { t } = useTranslation()
  if (uploading) return t('identity.detail.addPhoto.uploading', { uploaded, total })
  if (processing) return t('identity.detail.addPhoto.processing')
  return t('identity.detail.addPhoto.action')
}

export function AddPhotoButton({ state, onFiles }: AddPhotoButtonProps) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const busy = state.uploading || state.processing
  const label = useLabel(state)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        aria-busy={busy}
        onClick={() => input.current?.click()}
        className="w-full sm:w-auto"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Camera className="h-4 w-4" aria-hidden="true" />
        )}
        {label}
      </Button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        aria-label={t('identity.detail.addPhoto.action')}
        onChange={event => {
          const files = Array.from(event.target.files ?? [])
          // Reset first: picking the same file twice must fire `change` again.
          event.target.value = ''
          if (files.length > 0) onFiles(files)
        }}
      />
    </>
  )
}
